import {
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

import { PurchaseBill } from "../types";

interface PurchaseBillDetailsDialogProps {
  open: boolean;
  purchaseBill: PurchaseBill | null;
  onClose: () => void;
}

const money = (value?: number) => `Rs ${Number(value || 0).toLocaleString()}`;

const date = (value?: string) =>
  value ? new Date(value).toLocaleDateString() : "-";

const Detail = ({ label, value }: { label: string; value?: string | number }) => (
  <Stack spacing={0.5}>
    <Typography variant="caption" color="text.secondary" fontWeight={800}>
      {label.toUpperCase()}
    </Typography>
    <Typography fontWeight={700}>{value || "-"}</Typography>
  </Stack>
);

const statusColor = (status: PurchaseBill["status"]) => {
  if (status === "COMPLETED" || status === "RECEIVED") return "success";
  if (status === "CANCELLED") return "error";
  return "warning";
};

const PurchaseBillDetailsDialog = ({
  open,
  purchaseBill,
  onClose,
}: PurchaseBillDetailsDialogProps) => {
  if (!purchaseBill) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Typography variant="h6" fontWeight={900}>
              {purchaseBill.code}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Invoice {purchaseBill.invoiceNumber} • {purchaseBill.supplierName}
            </Typography>
          </Stack>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={3}>
            <Detail label="Supplier" value={purchaseBill.supplierName} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Detail label="Receiving Hub" value={purchaseBill.warehouseName || purchaseBill.warehouseId || "-"} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Detail label="Invoice Date" value={date(purchaseBill.invoiceDate)} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Detail label="Purchase Date" value={date(purchaseBill.purchaseDate)} />
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack spacing={0.5}>
              <Typography variant="caption" color="text.secondary" fontWeight={800}>
                STATUS
              </Typography>
              <Chip
                size="small"
                color={statusColor(purchaseBill.status)}
                label={purchaseBill.status}
                sx={{ width: "fit-content", fontWeight: 800 }}
              />
            </Stack>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={12} md={3}>
            <Detail label="Subtotal" value={money(purchaseBill.subtotal)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Detail label="Other Charges" value={money(purchaseBill.otherCharges)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Detail label="Total Amount" value={money(purchaseBill.totalAmount)} />
          </Grid>
          <Grid item xs={12} md={3}>
            <Detail label="Currency" value={purchaseBill.currency} />
          </Grid>

          <Grid item xs={12}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Spare Part</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Received</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {purchaseBill.items.map((item) => (
                  <TableRow key={item.id || item.sparePartId}>
                    <TableCell>{item.sparePartName}</TableCell>
                    <TableCell>{item.partCode}</TableCell>
                    <TableCell align="right">{item.quantity}</TableCell>
                    <TableCell align="right">{item.receivedQuantity ?? 0}</TableCell>
                    <TableCell align="right">{money(item.unitPrice)}</TableCell>
                    <TableCell align="right">{money(item.totalAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Grid>

          {purchaseBill.remarks && (
            <Grid item xs={12}>
              <Detail label="Remarks" value={purchaseBill.remarks} />
            </Grid>
          )}
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default PurchaseBillDetailsDialog;
